<template>
    <!-- Main container -->
    <div class="login-container">
        <AnimatedBackground/>

        <!-- Back to Home Link -->
        <router-link to="/" class="back-link">
            <ArrowLeft :size="20" />
            <span>Back to Home</span>
        </router-link>

        <!-- Card wrapper -->
        <div class="login-card">
            <!-- Header -->
            <h1>Curie</h1>
            <p class="subtitle">Login to continue</p>
            
            <!-- Login Form -->
            <form @submit.prevent="handleLogin">
                <!-- Email Field -->
                <div class="form-group">
                    <label for="email">Email</label>
                    <input
                        id="email"
                        v-model="email"
                        type="email"
                        placeholder="your.email@example.com"
                        required
                        autocomplete="email"
                    />
                </div>
                
                <!-- Password Field -->
                <div class="form-group">
                    <label for="password">Password</label>
                    <input
                        id="password"
                        v-model="password"
                        type="password"
                        placeholder="Enter your password"
                        required
                        autocomplete="current-password"
                    />
                </div>
                
                <!-- Error Message -->
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                </div>
                
                <!-- Submit Button -->
                <button 
                    type="submit" 
                    class="submit-btn"
                    :disabled="loading"
                >
                    {{ loading ? 'Logging in...' : 'Login' }}
                </button>
            </form>
            
            <!-- Register Link -->
            <p class="register-link">
                Don't have an account? 
                <router-link to="/register">Register here</router-link>
            </p>
        </div>
    </div>
</template>

<script>
// Import Vue functions
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AnimatedBackground from '@/components/AnimatedBackground.vue';
import { ArrowLeft } from 'lucide-vue-next';

export default {
    name: 'Login',
    components: {
        AnimatedBackground,
        ArrowLeft,
    },
    setup() {
        // ==================== SETUP ====================
        
        // Get router (for navigation after login)
        const router = useRouter();
        
        // Get auth store (for login function)
        const authStore = useAuthStore();
        
        // ==================== STATE (Reactive Data) ====================
        
        // ref() makes data reactive - when it changes, UI updates
        const email = ref('');
        const password = ref('');
        const loading = ref(false);
        const errorMessage = ref('');
        
        // ==================== FUNCTIONS ====================
        
        /**
         * Handle login form submission
         */
        const handleLogin = async () => {
            // Clear any previous errors
            errorMessage.value = '';
            
            // Set loading state (disables button, shows "Logging in...")
            loading.value = true;
            
            try {
                // Call the login function from auth store
                await authStore.login(email.value, password.value);
                
                // If successful, redirect to practice page
                router.push('/practice');
                
            } catch (error) {
                // If login fails, show error message
                errorMessage.value = error.message || 'Login failed. Please check your credentials.';
                
            } finally {
                // Always turn off loading state
                loading.value = false;
            }
        };
        
        // ==================== RETURN ====================
        // Return everything the template needs
        
        return {
            email,
            password,
            loading,
            errorMessage,
            handleLogin
        };
    }
};
</script>

<style scoped>
/* Font and global styling to match practice page */
* {
    font-family: "Space Grotesk", -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 400;
    letter-spacing: 1px;
}

/* Main container - centers everything with matching gradient */
.login-container {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--bg-primary);
    padding: 20px;
}

/* Card that holds the form - matching practice card style */
.login-card {
    background: rgba(var(--bg-primary-rgb), 1);
    padding: 40px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    width: 100%;
    max-width: 450px;
    z-index: 1;
}

/* Header */
h1 {
    margin: 0 0 10px 0;
    font-size: 32px;
    color: var(--text-secondary);
    text-align: center;
    font-weight: 600;
}

.subtitle {
    margin: 0 0 30px 0;
    color: rgba(var(--text-primary-rgb), 0.7);
    text-align: center;
    font-size: 15px;
}

/* Form Groups */
.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--text-secondary);
    font-size: 14px;
}

input {
    width: 100%;
    padding: 14px 16px;
    background: rgba(255, 246, 227, 0.05);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    font-size: 15px;
    color: var(--text-primary);
    transition: all 0.3s ease;
    box-sizing: border-box;
}

input::placeholder {
    color: rgba(var(--text-primary-rgb), 0.4);
}

input:hover, input:focus {
    outline: none;
    border-color: var(--accent);
    background: rgba(255, 246, 227, 0.08);
}

/* Error Message */
.error-message {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    font-size: 14px;
}

/* Submit Button - matching practice page buttons */
.submit-btn {
    width: 100%;
    padding: 16px;
    background: var(--text-primary);
    color: var(--bg-primary);
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    margin-top: 10px;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Register Link */
.register-link {
    text-align: center;
    margin-top: 24px;
    color: rgba(var(--text-primary-rgb), 0.7);
    font-size: 14px;
}

.register-link a {
    color: var(--text-secondary);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
}

.register-link a:hover {
    color: var(--text-primary);
    text-decoration: underline;
}

/* Back to Home Link */
.back-link {
    position: absolute;
    top: 20px;
    left: 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--accent);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: transform 0.2s ease;
    z-index: 10;
}

.back-link:hover {
    transform: translateX(-4px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .login-card {
        padding: 30px 24px;
        max-width: 100%;
    }

    h1 {
        font-size: 28px;
    }
}
</style>